import { Chip, IconButton, SxProps, Tooltip } from '@mui/material';
import React from 'react';
import { SIZES } from '../../constants/constants';
import type { ActionType } from '../ActionType';
import Loading from '../Loading';

type ResponsiveActionIconButtonLayout = 'bordered' | 'tooltip';

type ResponsiveActionIconButtonProps = {
    action: ActionType;
    index: number;
    layout: ResponsiveActionIconButtonLayout;
    iconSx?: SxProps;
    isAnyActionActive?: boolean;
};

export default function ResponsiveActionIconButton({
    action,
    index,
    layout,
    iconSx,
    isAnyActionActive = false
}: ResponsiveActionIconButtonProps) {
    const dataTestId = action.dataTestId || `icon-action-${index}`;
    const disabled = layout === 'bordered' ? action.disabled || isAnyActionActive : action.disabled;
    const onClick = layout === 'bordered' && action.disabled ? undefined : action.onClick;
    const iconButtonSx = { display: action.hidden ? 'none' : 'flex' };
    const iconButtonContent = action.loading ? (
        <Loading size="18px" />
    ) : layout === 'bordered' && action.label && typeof action.label !== 'string' ? (
        <Chip className="clickable" size={SIZES.small} label={action.label} variant={'outlined'} />
    ) : action.icon ? (
        <action.icon
            fontSize={SIZES.small}
            sx={{
                color: action.disabled ? 'text.disabled' : 'text.primary',
                ...iconSx
            }}
        />
    ) : null;
    const iconButtonCommonProps = {
        'aria-label': typeof action.label === 'string' ? action.label : undefined,
        'data-testid': dataTestId,
        className: 'icon-button',
        disabled,
        size: SIZES.small,
        component: layout === 'tooltip' ? ('span' as const) : undefined,
        onClick,
        sx: iconButtonSx
    };
    const iconButton = <IconButton {...iconButtonCommonProps}>{iconButtonContent}</IconButton>;

    if (layout === 'tooltip') {
        return <Tooltip title={action.label}>{iconButton}</Tooltip>;
    }

    return iconButton;
}
